// compatibility.schema.json source — `<control_root>/compatibility.json`
// (spec §3.2 workspace layout) capturing the M0 compatibility manifest
// (spec §18: "exact CLI version, Claude binary path/version/hash, pack
// version/digest, settings/policy digest, contract/schema versions,
// OS/runtime, supported flags/output events, command adapter IDs và smoke
// evidence paths").
//
// `settings_digest`/`profile_digest` (phase file Req 4, N36): written by
// mission-settings-profile.ts's computeSettingsProfileDigest (P3) — this
// schema is the validation contract for what P3 already produces, not a
// new digest algorithm. `permissions_allow_fallback` here records whether
// the *effective* fallback flag was active when this manifest was captured
// (distinct from config-schema.ts's default — this is the observed value).
import { z } from "zod";

export const CompatibilitySchema = z
  .object({
    schema_version: z.literal(1),
    captured_at: z.string().min(1),
    cli_version: z.string().min(1),
    claude_binary: z
      .object({
        path: z.string().min(1),
        version: z.string().min(1),
        hash: z.string().regex(/^[0-9a-f]{64}$/i),
      })
      .strict(),
    pack: z
      .object({
        version: z.string().min(1),
        digest: z.string().min(1),
      })
      .strict(),
    settings_digest: z.string().regex(/^[0-9a-f]{64}$/i),
    profile_digest: z.string().regex(/^[0-9a-f]{64}$/i),
    policy_digest: z.string().min(1),
    contract_version: z.string().min(1),
    schema_versions: z.record(z.string(), z.number().int().positive()),
    os: z.string().min(1),
    runtime: z.string().min(1),
    supported_flags: z.array(z.string()),
    supported_output_events: z.array(z.string()),
    command_adapter_ids: z.array(z.string()).min(1),
    smoke_evidence_paths: z.array(z.string()),
    // N18: observed value of the fallback flag at capture time — must equal
    // the effective-config value the mission actually ran with.
    permissions_allow_fallback: z.boolean(),
  })
  .strict();

export type Compatibility = z.infer<typeof CompatibilitySchema>;
